/**
 * F38 (Wave 2 Track D) — admin venue-subscriptions list page.
 *
 * Route: `/venue-subscriptions`
 * Permission: `payments:read` (super_admin + support + finance).
 *
 * Paginated per-venue subscription list with tier + status `<Select>` filters
 * (URL-param driven, mirroring the F23 commission-statements queue). Each row
 * links to the per-venue detail page. Read-only — overrides + grant-trial +
 * manual-invoice live on the detail page, gated on `venue_subscription:override`.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  EmptyState,
  Pagination,
  PlanTierBadge,
  Select,
  Skeleton,
  StatusChip,
  SubStateBadge,
  type SubState,
  Table,
  type TableColumn,
} from '@bravemove/ui-web';
import {
  SUBSCRIPTION_TIER_CODES,
  VENUE_SUBSCRIPTION_STATUSES,
  type SubscriptionTierCode,
  type VenueSubscriptionStatus,
} from '@bravemove/types';
import { adminSubscriptionApi, type AdminVenueSubscriptionListItem } from '../../api/subscription';
import { translateApiError } from '../../auth/errors';
import { PageHeader } from '../../components/PageHeader';

const PAGE_SIZE = 25;

/**
 * The F38 `SubStateBadge` primitive covers the four lifecycle states; `suspended`
 * is an admin-only terminal state with no primitive variant, so it keeps the
 * `StatusChip` (rejected tone). All other states render the badge.
 */
function isSubStateBadgeState(status: VenueSubscriptionStatus): status is SubState {
  return status !== 'suspended';
}

function formatDate(iso: string | null, locale: string): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function VenueSubscriptionsPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const tierParam = searchParams.get('tier') ?? 'all';
  const statusParam = searchParams.get('status') ?? 'all';
  const pageParam = Number(searchParams.get('page') ?? '1') || 1;

  const [rows, setRows] = useState<readonly AdminVenueSubscriptionListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminSubscriptionApi.listVenueSubscriptions({
        page: pageParam,
        perPage: PAGE_SIZE,
        // CST-002 — the backend's .strict() schema expects `tierCode` (not `tier`).
        ...(tierParam !== 'all' ? { tierCode: tierParam } : {}),
        ...(statusParam !== 'all' ? { status: statusParam } : {}),
      });
      setRows(data.data);
      setTotal(data.pagination.total);
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [tierParam, statusParam, pageParam, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchList();
  }, [fetchList]);

  const setParam = (key: string, value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value !== 'all') next.set(key, value);
      else next.delete(key);
      next.delete('page');
      return next;
    });
  };

  const onPageChange = (next: number) => {
    setSearchParams((prev) => {
      const np = new URLSearchParams(prev);
      np.set('page', String(next));
      return np;
    });
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const columns = useMemo<readonly TableColumn<AdminVenueSubscriptionListItem>[]>(
    () => [
      {
        key: 'venue',
        header: t('admin:subscription.list.col_venue', { defaultValue: 'Venue' }),
        cell: (row) => (
          <div className="flex flex-col gap-0.5">
            <a
              className="text-[13px] text-ink font-semibold hover:underline"
              href={`/venue-subscriptions/${row.venueId}`}
              onClick={(e) => {
                e.preventDefault();
                navigate(`/venue-subscriptions/${row.venueId}`);
              }}
              data-testid={`venue-subscriptions-link-${row.venueId}`}
            >
              {row.venueName ?? row.venueId}
            </a>
            {row.venueSlug ? (
              <span className="font-mono text-[11px] text-ink-3">{row.venueSlug}</span>
            ) : null}
          </div>
        ),
      },
      {
        key: 'tier',
        header: t('admin:subscription.list.col_tier', { defaultValue: 'Tier' }),
        cell: (row) => (
          <span data-testid={`venue-subscriptions-tier-${row.venueId}`}>
            <PlanTierBadge
              tier={row.tierCode as SubscriptionTierCode}
              label={t(`admin:subscription.tier.${row.tierCode}`, { defaultValue: row.tierCode })}
            />
          </span>
        ),
      },
      {
        key: 'cadence',
        header: t('admin:subscription.list.col_cadence', { defaultValue: 'Cadence' }),
        cell: (row) => (
          <span className="text-[12px] text-ink-2">
            {t(`admin:subscription.cadence.${row.billingCadence}`, {
              defaultValue: row.billingCadence,
            })}
          </span>
        ),
      },
      {
        key: 'status',
        header: t('admin:subscription.list.col_status', { defaultValue: 'Status' }),
        cell: (row) =>
          isSubStateBadgeState(row.status) ? (
            <SubStateBadge
              size="sm"
              state={row.status}
              label={t(`admin:subscription.status.${row.status}`, { defaultValue: row.status })}
            />
          ) : (
            <StatusChip
              size="sm"
              status="rejected"
              label={t(`admin:subscription.status.${row.status}`, { defaultValue: row.status })}
            />
          ),
      },
      {
        key: 'renews',
        header: t('admin:subscription.list.col_renews', { defaultValue: 'Renews / ends' }),
        cell: (row) => (
          <span className="text-[12px] text-ink-2">{formatDate(row.currentPeriodEnd, locale)}</span>
        ),
      },
    ],
    [t, locale, navigate],
  );

  return (
    <div className="space-y-6" data-testid="venue-subscriptions-page">
      <PageHeader
        title={t('admin:subscription.list.title', { defaultValue: 'Venue subscriptions' })}
        subtitle={t('admin:subscription.list.description', {
          defaultValue: 'Per-venue subscription tier, status, and renewal date.',
        })}
      />

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[180px]">
          <label
            className="block text-[11px] uppercase tracking-wider text-ink-3 mb-1"
            htmlFor="venue-subscriptions-tier-filter"
          >
            {t('admin:subscription.list.filter_tier', { defaultValue: 'Tier' })}
          </label>
          <Select
            id="venue-subscriptions-tier-filter"
            value={tierParam}
            data-testid="venue-subscriptions-tier-filter"
            aria-label={t('admin:subscription.list.filter_tier', { defaultValue: 'Tier' })}
            options={[
              {
                value: 'all',
                label: t('admin:subscription.list.filter_all', { defaultValue: 'All tiers' }),
              },
              ...SUBSCRIPTION_TIER_CODES.map((c) => ({
                value: c,
                label: t(`admin:subscription.tier.${c}`, { defaultValue: c }),
              })),
            ]}
            onChange={(e) => setParam('tier', e.target.value)}
          />
        </div>
        <div className="min-w-[180px]">
          <label
            className="block text-[11px] uppercase tracking-wider text-ink-3 mb-1"
            htmlFor="venue-subscriptions-status-filter"
          >
            {t('admin:subscription.list.filter_status', { defaultValue: 'Status' })}
          </label>
          <Select
            id="venue-subscriptions-status-filter"
            value={statusParam}
            data-testid="venue-subscriptions-status-filter"
            aria-label={t('admin:subscription.list.filter_status', { defaultValue: 'Status' })}
            options={[
              {
                value: 'all',
                label: t('admin:subscription.list.filter_all_status', {
                  defaultValue: 'All statuses',
                }),
              },
              ...VENUE_SUBSCRIPTION_STATUSES.map((s) => ({
                value: s,
                label: t(`admin:subscription.status.${s}`, { defaultValue: s }),
              })),
            ]}
            onChange={(e) => setParam('status', e.target.value)}
          />
        </div>
      </div>

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading ? (
        <div className="space-y-3" data-testid="venue-subscriptions-loading">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title={t('admin:subscription.list.empty', {
            defaultValue: 'No venue subscriptions in this view.',
          })}
        />
      ) : (
        <>
          <Table
            data={rows as AdminVenueSubscriptionListItem[]}
            columns={columns as TableColumn<AdminVenueSubscriptionListItem>[]}
            rowKey={(row) => row.venueId}
            onRowPress={(row) => navigate(`/venue-subscriptions/${row.venueId}`)}
          />
          {pageCount > 1 ? (
            <div className="flex justify-end">
              <Pagination
                page={pageParam}
                pageCount={pageCount}
                onChange={onPageChange}
                ariaLabel={t('admin:subscription.list.title', {
                  defaultValue: 'Venue subscriptions pagination',
                })}
              />
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

export default VenueSubscriptionsPage;
